import { ConfigProvider, theme, App as AntdApp } from "antd";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import {
  ADMIN_BATCHES,
  ADMIN_COURSES,
  ADMIN_USERS,
  ADMIN_USER_QUERIES,
  ADMIN_ADMIN_MANAGEMENT,
  ADMIN_CONFIG_MANAGEMENT,
  COURSE_MANAGEMENT_ROUTE_PREFIX,
  COURSE_MODULES_MANAGEMENT,
  ADMIN_DASHBOARD,
  HOME,
  LOGIN,
  NOT_FOUND,
  DISCORD_ANALYTICS_QUERY_ANALYTICS,
  DISCORD_ANALYTICS_QUERIES_DASHBOARD,
  CP_SHEET_PROBLEMS,
  DISCORD_ANALYTICS_EXPENSE_ANALYSIS,
  DISCORD_ANALYTICS_MENTOR_PERFORMANCE,
  DISCORD_ANALYTICS_MENTOR_DATA,
  ADMIN_EDUCATOR_MANAGEMENT,
  ADMIN_REFERRAL_DASHBOARD,
  SURVEY_SYSTEM_DASHBOARD,
  BULK_NOTIFICATIONS_CAMPAIGNS,
  BULK_NOTIFICATIONS_MAILING_LISTS,
  BULK_NOTIFICATIONS_MAILING_ASSETS,
  CP_SHEET_ANALYTICS_GENERAL,
  CP_SHEET_ANALYTICS_PROBLEM,
  CP_SHEET_ANALYTICS_USER,
  CP_SHEET_COMMENT_MODERATION,
  PAYMENTS_DISCOUNT_MANAGEMENT,
  ADMIN_SALES,
  COURSE_ANALYTICS,
  GLOBAL_CONTESTS_MANAGEMENT,
  PCD_SNIPPETS_MANAGEMENT,
  CP_SHEET_LEADERBOARD,
  WEBSITE_BLOGS_STATISTICS,
  WEBSITE_BLOGS_MANAGEMENT,
  PAYMENTS_LEADS,
  WEBSITE_ANNOUNCEMENTS_MANAGEMENT,
  CAREERS_MANAGEMENT,
  COURSE_SESSIONS_MANAGEMENT,
  COURSE_MODULES_LINKING,
  COURSE_CONTEST_MANAGEMENT,
  ADMIN_GUIDANCE_SESSIONS,
  WEBSITE_MENTORS_MANAGEMENT,
  WEBSITE_TESTIMONIALS_MANAGEMENT,
  WAITLIST_MANAGEMENT,
  WEBSITE_FEATURED_PROFILES_MANAGEMENT,
  EXTERNAL_PROBLEMS_MANAGEMENT,
  TAG_MAPPINGS,
  REWARD_KIT_CLAIMS
} from "./constants/routes";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import NotFound from "./pages/404";
import "./App.css";
import AdminManagement from "./pages/AdminManagement";
import AppConfigManagement from "./pages/AppConfigManagement";
import Batches from "./pages/Batches";
import BulkNotificationsCampaigns from "./pages/BulkNotifications/Campaigns";
import BulkNotificationsMailingAssets from "./pages/BulkNotifications/MailingAssets";
import BulkNotificationsMailingList from "./pages/BulkNotifications/MailingLists";
import CareersManagement from "./pages/CareersManagement";
import CourseAnalytics from "./pages/CourseAnalytics";
import CourseContestManagement from "./pages/CourseContentManagement/ContestManagement";
import CourseModulesManagement from "./pages/CourseContentManagement/Management";
import CourseModulesLinking from "./pages/CourseContentManagement/ModuleLinking";
import EducatorManagement from "./pages/CourseEducatorManagement";
import CourseManagement from "./pages/CourseManagement";
import Courses from "./pages/Courses";
import CourseSessionsManagement from "./pages/CourseSessionsManagement";
import CPSheetGeneralAnalytics from "./pages/CPSheet/Analytics/General";
import CPSheetProblemAnalytics from "./pages/CPSheet/Analytics/Problem";
import CPSheetUserAnalytics from "./pages/CPSheet/Analytics/User";
import CPSheetCommentModeration from "./pages/CPSheet/CommentModeration";
import CPSheetLeaderboard from "./pages/CPSheet/Leaderboard";
import CPSheetProblems from "./pages/CPSheet/Problems";
import Dashboard from "./pages/Dashboard";
import DiscordAnalyticsMentorsDashboard from "./pages/DiscordAnalytics/Mentors/Dashboard";
import DiscordAnalyticsMentorExpenseAnalysis from "./pages/DiscordAnalytics/Mentors/ExpenseAnalysis";
import DiscordAnalyticsMentorPerformance from "./pages/DiscordAnalytics/Mentors/Performance";
import DiscordAnalyticsQueryAnalytics from "./pages/DiscordAnalytics/Queries/Analytics";
import DiscordAnalyticsQueryDashboard from "./pages/DiscordAnalytics/Queries/Dashboard";
import ExternalProblemsManagement from "./pages/ExternalProblemsManagement";
import GlobalContestsManagement from "./pages/GlobalContestsManagement";
import PCDSnippetsManagement from "./pages/GlobalContestsManagement/PcdSnippets";
import GuidanceSessions from "./pages/GuidanceSessions";
import Home from "./pages/Home";
import Login from "./pages/Login";
import DiscountManagement from "./pages/Payments/DiscountManagement";
import PotentialLeadsDashboard from "./pages/Payments/Leads";
import Sales from "./pages/Payments/Sales";
import Queries from "./pages/Queries";
import ReferralDashboard from "./pages/ReferralDashboard";
import RewardKitClaims from "./pages/RewardKitClaims";
import SurveyDashboard from "./pages/SurveyDashboard";
import TagMappings from "./pages/TagMappings";
import Users from "./pages/Users";
import Waitlist from "./pages/Waitlist";
import WebsiteAnnouncementManagement from "./pages/WebsiteAnnouncements";
import WebsiteBlogsManagement from "./pages/WebsiteBlogs/Management";
import WebsiteBlogsStatistics from "./pages/WebsiteBlogs/Statistics";
import WebsiteFeaturedProfilesManagement from "./pages/WebsiteFeaturedProfilesManagement/index";
import WebsiteMentorsManagement from "./pages/WebsiteMentorsManagement";
import WebsiteTestimonialsManagement from "./pages/WebsiteTestimonialsManagement";
import { NotificationInit } from "./utils/notification";

const AppContent = () => {
  const router = createBrowserRouter([
    {
      path: HOME,
      element: <Home />
    },
    {
      path: ADMIN_DASHBOARD,
      element: <Dashboard />
    },
    {
      path: LOGIN,
      element: <Login />
    },
    {
      path: ADMIN_BATCHES,
      element: <Batches />
    },
    {
      path: ADMIN_COURSES,
      element: <Courses />
    },
    {
      path: ADMIN_SALES,
      element: <Sales />
    },
    {
      path: ADMIN_ADMIN_MANAGEMENT,
      element: <AdminManagement />
    },
    {
      path: ADMIN_CONFIG_MANAGEMENT,
      element: <AppConfigManagement />
    },
    {
      path: COURSE_MANAGEMENT_ROUTE_PREFIX + "/*",
      element: <CourseManagement />
    },
    {
      path: COURSE_MODULES_MANAGEMENT,
      element: <CourseModulesManagement />
    },
    {
      path: COURSE_MODULES_LINKING,
      element: <CourseModulesLinking />
    },
    {
      path: COURSE_CONTEST_MANAGEMENT,
      element: <CourseContestManagement />
    },
    {
      path: DISCORD_ANALYTICS_QUERY_ANALYTICS,
      element: <DiscordAnalyticsQueryAnalytics />
    },
    {
      path: DISCORD_ANALYTICS_QUERIES_DASHBOARD,
      element: <DiscordAnalyticsQueryDashboard />
    },
    {
      path: DISCORD_ANALYTICS_EXPENSE_ANALYSIS,
      element: <DiscordAnalyticsMentorExpenseAnalysis />
    },
    {
      path: DISCORD_ANALYTICS_MENTOR_PERFORMANCE,
      element: <DiscordAnalyticsMentorPerformance />
    },
    {
      path: DISCORD_ANALYTICS_MENTOR_DATA,
      element: <DiscordAnalyticsMentorsDashboard />
    },
    {
      path: CP_SHEET_PROBLEMS,
      element: <CPSheetProblems />
    },
    {
      path: ADMIN_USER_QUERIES,
      element: <Queries />
    },
    {
      path: ADMIN_USERS,
      element: <Users />
    },
    {
      path: ADMIN_EDUCATOR_MANAGEMENT,
      element: <EducatorManagement />
    },
    {
      path: ADMIN_REFERRAL_DASHBOARD,
      element: <ReferralDashboard />
    },
    {
      path: SURVEY_SYSTEM_DASHBOARD,
      element: <SurveyDashboard />
    },
    {
      path: BULK_NOTIFICATIONS_CAMPAIGNS,
      element: <BulkNotificationsCampaigns />
    },
    {
      path: BULK_NOTIFICATIONS_MAILING_LISTS,
      element: <BulkNotificationsMailingList />
    },
    {
      path: BULK_NOTIFICATIONS_MAILING_ASSETS,
      element: <BulkNotificationsMailingAssets />
    },
    {
      path: CP_SHEET_ANALYTICS_GENERAL,
      element: <CPSheetGeneralAnalytics />
    },
    {
      path: CP_SHEET_ANALYTICS_PROBLEM,
      element: <CPSheetProblemAnalytics />
    },
    {
      path: CP_SHEET_ANALYTICS_USER,
      element: <CPSheetUserAnalytics />
    },
    {
      path: CP_SHEET_COMMENT_MODERATION,
      element: <CPSheetCommentModeration />
    },
    {
      path: CP_SHEET_LEADERBOARD,
      element: <CPSheetLeaderboard />
    },
    {
      path: WEBSITE_BLOGS_STATISTICS,
      element: <WebsiteBlogsStatistics />
    },
    {
      path: WEBSITE_BLOGS_MANAGEMENT,
      element: <WebsiteBlogsManagement />
    },
    {
      path: PAYMENTS_DISCOUNT_MANAGEMENT,
      element: <DiscountManagement />
    },
    {
      path: PAYMENTS_LEADS,
      element: <PotentialLeadsDashboard />
    },
    {
      path: GLOBAL_CONTESTS_MANAGEMENT,
      element: <GlobalContestsManagement />
    },
    {
      path: PCD_SNIPPETS_MANAGEMENT,
      element: <PCDSnippetsManagement />
    },
    {
      path: WEBSITE_ANNOUNCEMENTS_MANAGEMENT,
      element: <WebsiteAnnouncementManagement />
    },
    {
      path: CAREERS_MANAGEMENT,
      element: <CareersManagement />
    },
    {
      path: COURSE_SESSIONS_MANAGEMENT,
      element: <CourseSessionsManagement />
    },
    {
      path: ADMIN_GUIDANCE_SESSIONS,
      element: <GuidanceSessions />
    },
    {
      path: WEBSITE_MENTORS_MANAGEMENT,
      element: <WebsiteMentorsManagement />
    },
    {
      path: WEBSITE_TESTIMONIALS_MANAGEMENT,
      element: <WebsiteTestimonialsManagement />
    },
    {
      path: WEBSITE_FEATURED_PROFILES_MANAGEMENT,
      element: <WebsiteFeaturedProfilesManagement />
    },
    {
      path: EXTERNAL_PROBLEMS_MANAGEMENT,
      element: <ExternalProblemsManagement />
    },
    {
      path: TAG_MAPPINGS,
      element: <TagMappings />
    },
    { path: COURSE_ANALYTICS, element: <CourseAnalytics /> },
    {
      path: WAITLIST_MANAGEMENT,
      element: <Waitlist />
    },
    {
      path: REWARD_KIT_CLAIMS,
      element: <RewardKitClaims />
    },
    {
      path: NOT_FOUND,
      element: <NotFound />
    }
  ]);

  const { isDarkMode } = useTheme();

  return (
    <ConfigProvider
      theme={{
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm
      }}
    >
      <AntdApp>
        <NotificationInit />
        <RouterProvider router={router} />
      </AntdApp>
    </ConfigProvider>
  );
};

const App = () => {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
};

export default App;
